// RC-1 Reusable RCE Consumer Framework — Calculator prefill hook.
//
// One hook to pre-fill calculator inputs from the Engineering Context
// Engine. Reads the binding directly from the catalog row — every field
// already has `{ key, label, unit, ... }`, the framework adds an optional
// `rcePath` per field. No schema migration required: rows without
// `rcePath` are left untouched.
//
// Precedence (matches CalculatorRunner's existing behaviour):
//
//     user edit  >  Reference Library pick  >  cross-calc handoff  >  RCE prefill  >  catalog default ("")
//
// The hook only contributes the lowest-precedence layer (the seed value).
// Library / handoff / user edits all happen *after* mount, in user code
// that already exists in CalculatorRunner; they naturally override the
// seed without any new precedence logic.

import { useMemo } from "react";
import { useEngineeringSession } from "./useEngineeringSession";
import { resolve } from "./resolver";
import { sessionToResolverCtx } from "./useReportRceWiring";

/** Pure helper — given a calculator definition and a resolver context,
 *  return a `{ [field.key]: prefilledValue }` map containing only the
 *  fields that successfully resolved. Used by the hook AND by the
 *  regression test (which calls it with a stub resolver). */
export function buildCalcPrefill(calc, ctx, resolveFn = resolve) {
  const seed = {};
  if (!calc || !Array.isArray(calc.fields)) return seed;
  for (const f of calc.fields) {
    if (!f || !f.rcePath) continue;
    const r = resolveFn(f.rcePath, ctx);
    if (!r) continue;
    if (r.confidence === "unknown" || r.confidence === "conflict") continue;
    if (r.value === "" || r.value === null || r.value === undefined) continue;
    seed[f.key] = String(r.value);
  }
  return seed;
}

/** Inventory the RCE-aware fields on a calculator. Returns the catalog
 *  fields that declare an `rcePath`. Used for the small "from Engineering
 *  Context · click to override" badge logic in the runner. */
export function listRceFields(calc) {
  if (!calc || !Array.isArray(calc.fields)) return [];
  return calc.fields.filter((f) => f && f.rcePath);
}

/**
 * React hook — returns:
 *
 *   {
 *     prefill: { [fieldKey]: stringValue, ... },   // values to seed
 *     ctx:     { ... resolver ctx ... },           // exposed for re-resolution
 *     hasRceBindings: boolean,
 *   }
 *
 * Re-evaluates only when the active Engineering Session changes.
 */
export function useCalcPrefill(calc) {
  const { session } = useEngineeringSession();
  const ctx = useMemo(
    () => sessionToResolverCtx(session),
    [
      session?.projectId, session?.rigId, session?.wellId,
      session?.bhaId, session?.drillStringId, session?.mudProgramId,
    ]
  );
  const prefill = useMemo(
    () => (session ? buildCalcPrefill(calc, ctx) : {}),
    [calc, ctx, session]
  );
  const hasRceBindings = useMemo(() => listRceFields(calc).length > 0, [calc]);
  return { prefill, ctx, hasRceBindings };
}
